import Ajax from '../Ajax'
import { store } from './Store'
import { Theme } from '../misc/Theme'
import { Alert, DisplayValue } from '..'
import { makeAutoObservable } from 'mobx'
import { Automator } from '../models/Automator'
import { addDays, format, isValid, subDays } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import { dict_key } from '../misc/DisplayDicts'


interface RunningAutomation {
    automator: Automator;
    parentUid: string;
}


export class AutomationStore {

    loadingLog: boolean = false;
    log: any[] = [];
    searchString: string = "";
    minDate: Date = subDays(new Date(), 27);
    maxDate: Date = addDays(new Date(), 1);
    searchAccountUids: string[] = []
    searchStatuses: string[] = []
    logResultLimit: number | "" = 300
    runningAutomation: RunningAutomation = null;
    options: any[] = [];

    constructor () {
        makeAutoObservable(this)
    }

    reset = () => {
        this.runningAutomation = null
    }

    run = async (data, mapId, automationNodeUid) => {
        await Ajax.Automation.Run(data, mapId, automationNodeUid).catch((response) => {
            Alert({message: response.data?.detail ? response.data?.detail : "Failed to run automation"})
        })
    }

    cancel = async (id = this.runningAutomation.automator.id) => {
        await Ajax.Automation.Cancel(id).catch((response) => {
            Alert({message: response.data?.detail ? response.data?.detail : "Failed to cancel automation"})
        })
    }

    init = async () => {
        await Ajax.Automation.Options().then((response) => {
            this.options = response.data
        }).catch(() => {
            Alert({message: "Unable to get Automation options"})
        })
    }

    getQualifyingOnlineForms = (enquiry) => {

        const ruleId = store.EnquiryStore.getQualifyingRule(enquiry)

        if (!ruleId || !this.options?.length) { 
            return []
        }
        
        var onlineFormsOptions = this.options.filter(option => option.qualifying_rules?.includes(ruleId))
        var applyFromAnywhereRules = ["UK.D.33.1", "UK.D.33.2", "UK.D.33.3", "UK.D.33.4", "UK.D.33.5", "UK.D.33.6"]
        var gbmRules = ["UK.D.33.4", "UK.D.33.5", "UK.D.33.6"]

        // Skilled Worker
        if (applyFromAnywhereRules.includes(ruleId)) {
            var outcomeData = enquiry?.additionalOutcomeData
            
            if (outcomeData) {
                if (outcomeData && "applying_from_uk" in outcomeData && outcomeData["applying_from_uk"]) {
                    onlineFormsOptions = onlineFormsOptions.filter(option => {
                        return !["SWV_EC", "GBM_EC", "COS_APPLY", "COS_ASSIGN_D"].includes(option.id)
                    })
                }
                if (outcomeData && "applying_from_uk" in outcomeData && !outcomeData["applying_from_uk"]) {
                    onlineFormsOptions = onlineFormsOptions.filter(option => {
                        return !["SWV_FLR", "GBM_EXTEND", "COS_ASSIGN"].includes(option.id) || (gbmRules.includes(ruleId) && option.id === "COS_ASSIGN")
                    })
                }
            }
        }

        return onlineFormsOptions
    }

    getUpdatedAutomator = (automator: Automator) => {

        // Check runningAutomator for updates
        if (this.runningAutomation && this.runningAutomation.automator?.id === automator.id) {
            automator = this.runningAutomation.automator
        } else if (store.NodeStore.leafNode.automations) {
            // Check automations under the leafNode for the completed automation.
            // This should cover Anquiry and Automation leaf nodes.
            automator = store.NodeStore.leafNode?.automations?.find((a: Automator) => a.id === automator.id)
        }

        return automator
    }

    handleAutomationNotification = async (message) => {

        console.log("AutomationNotification", message)

        var { leafNode } = store.NodeStore
        var { parentUid, automator } = message

        // Handle finished automations
        if (["INSTANTIATED", "RUNNING"].includes(automator.status)) {
            
            // Refresh on first poll of the standalone autoamtion
            if (!this.runningAutomation && !parentUid.includes("ENQUIRY")) {
                store.NodeStore.refreshFolderNode()
            }

            this.runningAutomation = message
        
        } else {

            this.runningAutomation = null
        
            if (leafNode?.uid === parentUid) {
                await store.NodeStore.refreshLeafNode()
            } else {

                var label = DisplayValue("automation_status", automator.status)
                var color = this.statusColor(automator.status, true)

                Alert({
                    message: `${label}`,
                    persist: true,
                    color: color,
                    actions: [{label: "Go to Automation", color: "info", action: async () => {
                        store.NodeStore.navigateNode(parentUid)
                    }}]
                })
            }
        }
    }

    statusColor = (status: string, mui: boolean = false) => {

        var color = "info"

        switch (status) {
            case "INSTANTIATED":
            case "RUNNING":
            case "CANCELLED":
                return mui ? "info" : Theme.palette.info.contrastText
            case "FINISHED":
                color = "success"
                break
            case "PROMPT_TIMEOUT":
                color = "warning"
                break
            case "FAIL":
            case "ERROR":
            case "TIMEOUT":
            case "WEBSITE_ERROR":
            case "MAPPING_ERROR":
            case "VALIDATION_ERROR":
                color = "error"
                break
        }

        return mui ? color : Theme.palette[color].main
    }

    statusDescription = (automator: Automator) => {

        if (automator.status_message) {
            return automator.status_message
        }

        switch (automator.status) {
            case "INSTANTIATED":
                return "This Automation in a queue, this should only take a minute."

            case "RUNNING":
                return "This Automation is running."

            case "FINISHED":
                return ""

            case "TIMEOUT":
                return "This Automation was taking too long to run so it was cancelled."

            case "PROMPT_TIMEOUT":
                return "The user took long to respond to a prompt."
    
            case "VALIDATION_ERROR":
                return "Some of your input data failed validation in the online form."
            
            case "WEBSITE_ERROR":
            case "HOME_OFFICE_ERROR":
                return "The target website of the Automation is having some problems at the moment, please try again later."
    
            case "CANCELLED":
                return "This Automation was cancelled."
    
            case "MAPPING_ERROR":
                return "There was an unexpected page which our software could not automate."
    
            case "ERROR":
                return "There was an unexpected error."
            
            default:
                return "Unkown status."
        }
    }

    // #region Log features

    getLog = async () => {
        this.loadingLog = true
        
        var minDate = isValid(this.minDate) ? format(this.minDate, "yyyy-MM-dd") : "1900-01-01"
        var maxDate = isValid(this.maxDate) ? format(this.maxDate, "yyyy-MM-dd") : "2100-01-01"

        await Ajax.Automation.GetLog(minDate, maxDate).then((response) => {
            this.log = response.data
        }).catch(() => {
            Alert({message: "Failed to get Automation log"})
        })

        this.loadingLog = false
    }
    
    setSearchString = (str: string) => {
        this.searchString = str
    }

    setMinDate = (date: Date) => {
        this.minDate = date
    }

    setMaxDate = (date: Date) => {
        this.maxDate = date
    }

    setLogResultLimit = (logResultLimit: number | "") => {
        this.logResultLimit = logResultLimit
    }

    setSearchAccountUids = (searchAccountUids: string[]) => {
        this.searchAccountUids = searchAccountUids
    }

    setSearchStatuses = (searchStatuses: string[]) => {
        this.searchStatuses = searchStatuses
    }

    get getFilteredLog () {
        if (!this.log?.length || !Array.isArray(this.log)) {
            return []
        }
        
        return this.log?.filter(logItem => {

            if (!this.searchAccountUids?.includes(logItem.sponsor?.uid) && !this.searchAccountUids?.includes(logItem.account?.uid)) return false

            if (!this.searchStatuses?.includes(logItem.status)) return false

            if (!this.searchString) return true

            if (logItem.label?.toLowerCase().includes(this.searchString?.toLowerCase())) return true

            if (logItem.advisorEmail?.toLowerCase().includes(this.searchString?.toLowerCase())) return true

            return false
        })
    }

    downloadLog = async () => {

        var rows = this.getFilteredLog
        if (!rows?.length) {
            return
        }
        var csvRows = []
        var headers = Object.keys(rows[0])
        var headerLabels = Object.keys(rows[0]).map(key => DisplayValue("dict_key", key))
        csvRows.push(headerLabels.join(','))

        for (const row of rows) {
            const values = headers.map(header => {
                var value = row[header]
                if (header.endsWith("At")) {
                    var dateValue = new Date(value)
                    if (isValid(dateValue)) {
                        value = format(dateValue, "yyyy-MM-dd HH:mm:ss")
                    }
                }
                if (typeof value === "string") {
                    value = ('' + value).replace(/"/g, '\\"')
                    value = value === "null" ? "" : value
                }
                if (typeof value === "object" && value?.name || value?.uid) {
                    value = value?.name ? value.name : value.uid
                }
                return `"${value}"`
            })
            csvRows.push(values.join(','))
        }
        const csvString = csvRows.join('\n')

        // 2. Create a Blob
        const blob = new Blob([csvString], { type: 'text/csvcharset=utf-8' })

        // 3. Create a temporary URL
        const url = URL.createObjectURL(blob)

        // 4. Create and trigger a download link
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `automation_log_${format(new Date(), "yyyy-MM-dd")}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the temporary URL
        URL.revokeObjectURL(url)
    }
}